import { ErrorDetailsHttp, ErrorJSON } from '../errors';

export class AtomicError extends Error {
    details?: AtomicErrorDetails;
    extensions?: AtomicErrorExtensions;

    constructor(
        message: string,
        details?: AtomicErrorDetails,
        extensions?: AtomicErrorExtensions
    ) {
        super(message);
        this.name = this.constructor.name;
        this.details = details;
        this.extensions = extensions;
        // TypeScript requires a workaround when extending built-ins like Error
        // Ref: https://github.com/Microsoft/TypeScript-wiki/blob/main/Breaking-Changes.md#extending-built-ins-like-error-array-and-map-may-no-longer-work
        Object.setPrototypeOf(this, AtomicError.prototype);
    }

    toJSON(): ErrorJSON {
        return {
            name: this.name,
            message: this.message,
            stack: this.stack,
            details: this.details,
            extensions: this.extensions,
        };
    }
}

export interface AtomicErrorDetails extends Record<string, unknown> {
    http?: ErrorDetailsHttp;
}

export interface AtomicErrorExtensions extends Record<string, unknown> {
    code?: AtomicErrorCode;
    custom_code?: CustomAtomicErrorCode;
}

export interface AtomicErrorResponseBody {
    trace_id?: string;
    code?: AtomicErrorCode;
    user_message?: string;
    developer_message?: string;
    custom_code?: CustomAtomicErrorCode;
    /** used as a fallback when the Atomic API doesn't return JSON */
    text?: string;
}

/** These are the official error codes supplied by Atomic. */
type AtomicErrorCode =
    // Generic error codes.
    | 'GEN0000' // Generic failure for invalid request.
    | 'GEN0001' // Generic internal server error.
    | 'GEN0002' // Generic failure for feature that will be available in upcoming release.
    | 'GEN0003' // Generic failure for an API that is not entitled to your API key.
    // User Error Codes (prefix U)
    | 'U0000' // User does not exist.
    | 'U0002' // Bad query parameters (GET /users, GET /users/{id})
    | 'U0003' // Validation of request body failed (POST /users)
    | 'U0004' // Bad request for email/phone verification.
    | 'U0005' // User already exists.
    | 'U0006' // Identity verification failed
    | 'U0007' // The user has not yet signed or acknowledged all necessary agreements.
    | 'U0008' // The user has too many unexpired session tokens. For security purposes, either wait for the tokens to expire or revoke the existing ones.
    | 'U0009' // The token was not found or has expired.
    | 'U0010' // A user with this email has already been verified. Each email must be unique.
    | 'U0011' // User is already verified
    /* Corporate error codes */
    | 'CO0001' // Unable to create a corporate, `custom_id` is the minimum field required.
    | 'CO0002' // Unable to create a corporate because the `custom_id` is already in use.
    | 'CO0003' // Unable to create or modify corporate because validation of `financial_profile` failed.
    | 'CO0004' // Unable to create or modify corporate because validation of `tax_profile` failed.
    | 'CO0005' // Unable to create or modify corporate because validation of `identity` (excluding address) failed.
    | 'CO0006' // Unable to create or modify corporate because validation of `identity.address` failed.
    | 'CO0007' // Unable to modify corporate, `custom_id` cannot be modified.
    | 'CO0008' // Unable to modify corporate, `corporate_id` does not exist.
    | 'CO0009' // Unable to delete corporate, `corporate_id` does not exist.
    | 'CO0010' // Unable to modify corporate because of its current verification status.
    /* Participant error codes */
    | 'PA0001' // Unable to create participant, `account_id` is the minimum field required.
    | 'PA0002' // Investment account does not exist.
    | 'PA0003' // Participant does not exist.
    | 'PA0004' // Unable to modify participant, `account_id` cannot be modified.
    | 'PA0005' // Unable to modify participant, `corporate_id` cannot be modified.
    | 'PA0006' // Unable to modify participant because of its current verification status.
    /* Transaction Endpoints */
    | 'TR0000' // The transaction does not exist.
    | 'TR0001' // The transaction cannot be created because the user has not yet successfully completed the KYC/CIP program.
    | 'TR0002' // The transaction cannot be created because the user does not own the external account.
    | 'TR0003' // The trade cannot be completed because the user does not have enough shares to fill the order.
    | 'TR0004' // The transaction cannot be completed because the external account for this transaction does not have enough balance.
    | 'TR0005' // The trade cannot be completed because user has exceeded the rate limit
    | 'TR0006' // The trade cannot be completed because the cusip specified is not tradable
    /* Ledger Endpoints */
    | 'L0001' // No pending transactions in ledger.
    | 'L0002' // Cannot use an already committed ledger.
    | 'L0003' // Cannot commit a ledger with planned execution date in the future.
    /* Account Endpoints */
    | 'AC0000' // Account does not exist.
    | 'AC0001' // A user already has an account of this type.
    | 'AC0002' // Attempt to delete account with active sleeves.
    /* Sleeve Endpoints */
    | 'SL0000' // Sleeve does not exist.
    | 'SL0001' // A sleeve with a balance cannot be deleted.
    | 'SL0002' // Temporarily unable to get sleeve balance.
    /*  External Account Endpoints */
    | 'EA0000' // External account not found
    | 'EA0001' // Insufficient funds available.
    // Asset endpoint errors
    | 'A0000' // Bad request, ticker symbol is not supported by Atomic.
    | 'A0001' // Bad request, query parameter is malformed.
    // Verification errors
    | 'VRF0002'; // There is already a verification with no required steps left

/** These error codes cover cases where Atomic's code is not precise enough for programattic use. */
type CustomAtomicErrorCode = 'CUSTOM_ERROR'; // TODO: Finish implementing this.
